import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  UpcomingTraining,
  DashboardAnnouncement,
  DashboardUpcomingTrainingsParams,
  DashboardAnnouncementsParams,
  PaginatedResponse,
  DashboardSummaryApiResponse,
  DashboardSummary,
  DashboardEducationStatus,
  DashboardCategorySuccessCompletion,
  EducationMapResponse,
  ProvinceEducationDetailResponse,
} from "../types/dashboard.types";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const MOCK_UPCOMING_TRAININGS: UpcomingTraining[] = [
  {
    id: "1",
    trainingName: i18n.t("mockData.education.osgBasic2026"),
    format: "Yüz Yüze",
    registrationCount: 23,
    maxRegistrations: 100,
    publicationDate: "12.01.2026",
    status: "active",
  },
  {
    id: "2",
    trainingName: i18n.t("mockData.education.projectManagementAdvanced"),
    format: "Üniteli",
    registrationCount: 120,
    maxRegistrations: 150,
    publicationDate: "15.01.2026",
    status: "request_collection",
  },
  {
    id: "3",
    trainingName: i18n.t("mockData.education.itCyberSecurity"),
    format: "Canlı",
    registrationCount: 49,
    maxRegistrations: 50,
    publicationDate: "18.01.2026",
    status: "closed",
  },
  {
    id: "4",
    trainingName: i18n.t("mockData.education.leadershipTeam"),
    format: "Yüz Yüze",
    registrationCount: 0,
    maxRegistrations: null,
    publicationDate: "20.01.2026",
    status: "approval_pending",
  },
  {
    id: "5",
    trainingName: i18n.t("mockData.education.environmentSustainability"),
    format: "Üniteli",
    registrationCount: 0,
    maxRegistrations: null,
    publicationDate: "22.01.2026",
    status: "budget_pending",
  },
  {
    id: "6",
    trainingName: i18n.t("mockData.education.communicationSkills"),
    format: "Canlı",
    registrationCount: 30,
    maxRegistrations: 40,
    publicationDate: "25.01.2026",
    status: "active",
  },
  {
    id: "7",
    trainingName: i18n.t("mockData.education.financialLiteracy"),
    format: "Yüz Yüze",
    registrationCount: 18,
    maxRegistrations: 25,
    publicationDate: "28.01.2026",
    status: "request_collection",
  },
  {
    id: "8",
    trainingName: i18n.t("mockData.education.iso9001Mgmt"),
    format: "Üniteli",
    registrationCount: 45,
    maxRegistrations: 50,
    publicationDate: "01.02.2026",
    status: "active",
  },
  {
    id: "9",
    trainingName: i18n.t("mockData.education.fireSafety"),
    format: "Yüz Yüze",
    registrationCount: 0,
    maxRegistrations: null,
    publicationDate: "05.02.2026",
    status: "approval_pending",
  },
  {
    id: "10",
    trainingName: "Etik ve Uyum",
    format: "Canlı",
    registrationCount: 60,
    maxRegistrations: 80,
    publicationDate: "10.02.2026",
    status: "active",
  },
];

const MOCK_ANNOUNCEMENTS: DashboardAnnouncement[] = [
  {
    id: "1",
    imageUrl: "https://picsum.photos/seed/d1/80/80",
    title: i18n.t("mockData.announcement.systemMaintenance"),
    time: "13:45",
    tag: i18n.t("mockData.tags.systemMaintenance"),
    tagType: "system",
  },
  {
    id: "2",
    imageUrl: "https://picsum.photos/seed/d2/80/80",
    title: i18n.t("mockData.announcement.osgReminder"),
    time: "21:32",
    tag: i18n.t("mockData.tags.reminder"),
    tagType: "reminder",
  },
  {
    id: "3",
    imageUrl: "https://picsum.photos/seed/d3/80/80",
    title: i18n.t("mockData.announcement.libraryCatalog"),
    time: "12.02.2026",
    tag: "Yeni Katalog",
    tagType: "catalog",
  },
  {
    id: "4",
    imageUrl: "https://picsum.photos/seed/d4/80/80",
    title: i18n.t("mockData.announcement.yearlyPlan"),
    time: "11.02.2026",
    tag: "Eğitim",
    tagType: "education",
  },
  {
    id: "5",
    imageUrl: "https://picsum.photos/seed/d5/80/80",
    title: i18n.t("mockData.announcement.keypUpdate"),
    time: "10.02.2026",
    tag: i18n.t("mockData.tags.systemMaintenance"),
    tagType: "system",
  },
  {
    id: "6",
    imageUrl: "https://picsum.photos/seed/d6/80/80",
    title: i18n.t("mockData.announcement.certRenewal"),
    time: "09.02.2026",
    tag: i18n.t("mockData.tags.reminder"),
    tagType: "reminder",
  },
];

// Pad mock data to simulate 100 total for trainings
function padTrainings(total: number): UpcomingTraining[] {
  const result = [...MOCK_UPCOMING_TRAININGS];
  while (result.length < total) {
    const i = result.length % MOCK_UPCOMING_TRAININGS.length;
    result.push({
      ...MOCK_UPCOMING_TRAININGS[i],
      id: `clone-${result.length}`,
      trainingName: `${MOCK_UPCOMING_TRAININGS[i].trainingName} (${result.length})`,
    });
  }
  return result;
}

const PADDED_TRAININGS = padTrainings(100);

const unwrapApiData = <T>(response: unknown): T => {
  const value = response as { data?: unknown };
  if (value?.data && typeof value.data === "object" && "data" in value.data) {
    return (value.data as { data: T }).data;
  }
  if (value?.data !== undefined) return value.data as T;
  return response as T;
};

async function getDashboardSummary(params?: {
  /** Chart verilerini son N gün ile sınırla (BE desteği beklendiğinde aktifleşir). */
  periodDays?: number;
}): Promise<{
  summary: DashboardSummary;
  educationStatus: DashboardEducationStatus;
  categorySuccessCompletion: DashboardCategorySuccessCompletion;
}> {
  const { data } = await apiClient.get<DashboardSummaryApiResponse>(
    "/api/admin/dashboard",
    {
      params: params?.periodDays
        ? { periodDays: params.periodDays }
        : undefined,
    },
  );

  if (!data?.data) {
    throw new Error("Dashboard summary not found");
  }

  return data.data;
}

async function getEducationMap(params?: {
  endDate?: string;
  period?: string;
  startDate?: string;
}): Promise<EducationMapResponse> {
  const response = await apiClient.get("/api/dashboard/education-map", {
    params,
    skipErrorToast: true,
  });

  return unwrapApiData<EducationMapResponse>(response.data);
}

async function getEducationMapProvince(
  provinceId: string,
  params?: {
    endDate?: string;
    page?: number;
    pageSize?: number;
    period?: string;
    startDate?: string;
  },
): Promise<ProvinceEducationDetailResponse> {
  const response = await apiClient.get(
    `/api/dashboard/education-map/provinces/${provinceId}`,
    {
      params,
      skipErrorToast: true,
    },
  );

  return unwrapApiData<ProvinceEducationDetailResponse>(response.data);
}

export const dashboardApi = {
  /**
   * GET /api/admin/dashboard - top summary + charts for admin dashboard.
   */
  getDashboardSummary,
  /**
   * GET /api/dashboard/education-map - regional education map summary.
   */
  getEducationMap,
  /**
   * GET /api/dashboard/education-map/provinces/:provinceId - selected province trainings.
   */
  getEducationMapProvince,
  /**
   * Get upcoming trainings with pagination (for dashboard table)
   */
  getUpcomingTrainings: async (
    params?: DashboardUpcomingTrainingsParams,
  ): Promise<PaginatedResponse<UpcomingTraining>> => {
    await delay(300);
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const start = (page - 1) * pageSize;
    const data = PADDED_TRAININGS.slice(start, start + pageSize);
    return {
      data,
      total: PADDED_TRAININGS.length,
      page,
      pageSize,
    };
  },

  /**
   * Get announcements for dashboard list (general/private tab, paginated)
   */
  getAnnouncements: async (
    params?: DashboardAnnouncementsParams,
  ): Promise<PaginatedResponse<DashboardAnnouncement>> => {
    await delay(300);
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 5;
    const tab = params?.tab ?? "general";
    let list = MOCK_ANNOUNCEMENTS;
    if (tab === "private") {
      list = MOCK_ANNOUNCEMENTS.filter((a) => a.tagType === "reminder");
    }
    const start = (page - 1) * pageSize;
    const data = list.slice(start, start + pageSize);
    return {
      data,
      total: list.length,
      page,
      pageSize,
    };
  },
};
